/* Mobile card for a book feed item. Cover left, content right, always-visible action buttons. */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { MoreHorizontal, Star } from "lucide-react";
import { ThumbnailPlaceholder } from "@/components/feed/ThumbnailPlaceholder";
import type { FeedItemData } from "@/components/feed/FeedItemRow";

interface BookItemMobileCardProps {
  item: FeedItemData;
  onOpen?: () => void;
  onDelete?: () => void;
  onComplete?: () => void;
  onQueue?: () => void;
  onBacklog?: () => void;
}

export function BookItemMobileCard({ item, onOpen, onDelete, onComplete, onQueue, onBacklog }: BookItemMobileCardProps) {
  const [isPending, setIsPending] = useState(false);

  const currentPage = item.bookCurrentPage ?? 0;
  const totalPages = item.bookTotalPages ?? 1;
  const progressPercent = Math.round((currentPage / totalPages) * 100);
  const coverUrl = item.bookCover ?? item.thumbnail;

  function runAction(action?: () => void) {
    if (!action) return;
    setIsPending(true);
    action();
  }

  if (isPending) return null;

  return (
    <div data-slot="feed-card" className="flex flex-col gap-2 px-4 py-3">
      {/* Cover + info row */}
      <div className="flex gap-3">
        <div
          className="shrink-0 w-[60px] h-[90px] cursor-pointer"
          onClick={() => onOpen?.()}
        >
          {coverUrl ? (
            <img
              src={coverUrl}
              alt={item.title}
              className="w-full h-full rounded-sm object-cover bg-muted"
            />
          ) : (
            <ThumbnailPlaceholder contentType="book" className="rounded-sm" />
          )}
        </div>

        <div className="flex flex-col gap-1.5 min-w-0 flex-1">
          <p
            className="text-base font-medium leading-snug cursor-pointer active:opacity-70"
            onClick={() => onOpen?.()}
          >
            {item.sourceStarred && <Star className="inline size-3 fill-current text-primary mr-1 align-baseline" />}
            {item.title}
          </p>

          {item.bookAuthor && (
            <span className="text-xs text-muted-foreground">
              {item.bookAuthor}
            </span>
          )}

          {item.bookPageStart != null && item.bookPageEnd != null && (
            <span className="text-xs text-muted-foreground">
              Pages {item.bookPageStart}–{item.bookPageEnd}
              {item.bookPagesPerDay != null && ` · ${item.bookPagesPerDay} pages/day`}
            </span>
          )}

          {/* Progress bar */}
          <div className="flex items-center gap-2 w-full">
            <Progress value={progressPercent} className="h-1.5 flex-1" />
            <span className="text-[11px] text-muted-foreground shrink-0">
              {progressPercent}%
            </span>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          className="text-xs h-7 px-2"
          onClick={() => runAction(onComplete)}
        >
          <kbd className="opacity-60 mr-1">c</kbd> complete
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-xs h-7 px-2"
          onClick={() => runAction(onQueue)}
        >
          <kbd className="opacity-60 mr-1">q</kbd> queue
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-xs h-7 px-2"
          onClick={() => runAction(onBacklog)}
        >
          <kbd className="opacity-60 mr-1">b</kbd> backlog
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-xs h-7 px-2"
          onClick={() => runAction(onDelete)}
        >
          <kbd className="opacity-60 mr-1">x</kbd> dismiss
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-xs h-7 px-1.5"
        >
          <MoreHorizontal className="size-4" />
        </Button>
      </div>
    </div>
  );
}
